import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { HelpCircle, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Save, X, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = { pregunta: "", respuesta: "", orden: 999, activa: true };

export default function FaqsManager() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadFaqs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/faqs/all`, { withCredentials: true });
      setFaqs(res.data);
    } catch {
      setError("Error al cargar las FAQs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadFaqs(); }, [loadFaqs]);

  const handleSave = async () => {
    if (!form.pregunta.trim() || !form.respuesta.trim()) {
      setError("La pregunta y la respuesta son obligatorias."); return;
    }
    setSaving(true); setError("");
    try {
      if (editingId) {
        await axios.put(`${API}/faqs/${editingId}`, form, { withCredentials: true });
        setSuccess("FAQ actualizada correctamente.");
      } else {
        const nextOrden = faqs.length > 0 ? Math.max(...faqs.map(f => f.orden || 0)) + 1 : 1;
        await axios.post(`${API}/faqs`, { ...form, orden: nextOrden }, { withCredentials: true });
        setSuccess("FAQ creada correctamente.");
      }
      setForm(EMPTY_FORM); setEditingId(null); setShowForm(false);
      loadFaqs();
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Error al guardar la FAQ.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta FAQ?")) return;
    try {
      await axios.delete(`${API}/faqs/${id}`, { withCredentials: true });
      setSuccess("FAQ eliminada."); loadFaqs();
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Error al eliminar.");
    }
  };

  const handleToggle = async (faq) => {
    try {
      await axios.put(`${API}/faqs/${faq._id}`, { activa: !faq.activa }, { withCredentials: true });
      loadFaqs();
    } catch {
      setError("Error al actualizar visibilidad.");
    }
  };

  const handleMoveOrder = async (faq, direction) => {
    const sorted = [...faqs].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    const idx = sorted.findIndex(f => f._id === faq._id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swapFaq = sorted[swapIdx];
    try {
      await Promise.all([
        axios.put(`${API}/faqs/${faq._id}`, { orden: swapFaq.orden }, { withCredentials: true }),
        axios.put(`${API}/faqs/${swapFaq._id}`, { orden: faq.orden }, { withCredentials: true }),
      ]);
      loadFaqs();
    } catch {
      setError("Error al reordenar.");
    }
  };

  const startEdit = (faq) => {
    setForm({ pregunta: faq.pregunta, respuesta: faq.respuesta, orden: faq.orden, activa: faq.activa });
    setEditingId(faq._id); setShowForm(true); setError("");
  };

  const cancelForm = () => {
    setForm(EMPTY_FORM); setEditingId(null); setShowForm(false); setError("");
  };

  const sortedFaqs = [...faqs].sort((a, b) => (a.orden || 0) - (b.orden || 0));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A]">Preguntas Frecuentes</h1>
          <p className="font-['Montserrat'] text-[#475569] text-sm mt-1">Gestiona las FAQs visibles en la landing page</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); setError(""); }}
          data-testid="add-faq-btn"
          className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-bold px-5 py-2.5 rounded-xl hover:bg-[#005A80] transition-all shadow-sm"
        >
          <Plus size={18} /> Nueva FAQ
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="faqs-error">
          <AlertCircle size={16} color="#EF4444" />
          <p className="font-['Montserrat'] text-red-600 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3" data-testid="faqs-success">
          <HelpCircle size={16} color="#3CB44B" />
          <p className="font-['Montserrat'] text-green-700 text-sm">{success}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-sm" data-testid="faq-form">
          <h3 className="font-['Montserrat'] font-semibold text-lg text-[#0F172A] mb-4">
            {editingId ? "Editar FAQ" : "Nueva FAQ"}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block font-['Montserrat'] font-bold text-[#0F172A] text-sm mb-1">Pregunta *</label>
              <input
                value={form.pregunta}
                onChange={e => setForm(p => ({ ...p, pregunta: e.target.value }))}
                placeholder="¿Cuál es tu pregunta?"
                data-testid="faq-pregunta-input"
                className="w-full border-2 border-[#E2E8F0] rounded-xl px-4 py-3 font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] transition-colors"
              />
            </div>
            <div>
              <label className="block font-['Montserrat'] font-bold text-[#0F172A] text-sm mb-1">Respuesta *</label>
              <textarea
                value={form.respuesta}
                onChange={e => setForm(p => ({ ...p, respuesta: e.target.value }))}
                placeholder="Escribe la respuesta completa..."
                rows={4}
                data-testid="faq-respuesta-input"
                className="w-full border-2 border-[#E2E8F0] rounded-xl px-4 py-3 font-['Montserrat'] text-sm focus:outline-none focus:border-[#0072A4] transition-colors resize-none"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.activa}
                onChange={e => setForm(p => ({ ...p, activa: e.target.checked }))}
                data-testid="faq-activa-checkbox"
                className="w-4 h-4 accent-[#0072A4]"
              />
              <span className="font-['Montserrat'] text-[#475569] text-sm">Visible en la web</span>
            </label>
          </div>
          <div className="flex gap-3 mt-5">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="faq-save-btn"
              className="flex items-center gap-2 bg-[#0072A4] text-white font-['Montserrat'] font-bold px-6 py-2.5 rounded-xl hover:bg-[#005A80] disabled:opacity-60 transition-all"
            >
              <Save size={16} /> {saving ? "Guardando..." : "Guardar"}
            </button>
            <button
              onClick={cancelForm}
              data-testid="faq-cancel-btn"
              className="flex items-center gap-2 border-2 border-[#E2E8F0] text-[#475569] font-['Montserrat'] font-bold px-6 py-2.5 rounded-xl hover:bg-[#F8FAFC] transition-all"
            >
              <X size={16} /> Cancelar
            </button>
          </div>
        </div>
      )}

      {/* FAQs List */}
      {loading ? (
        <div className="text-center py-12 text-[#475569] font-['Montserrat']">Cargando FAQs...</div>
      ) : sortedFaqs.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-[#E2E8F0]">
          <HelpCircle size={48} color="#CBD5E1" className="mx-auto mb-3" />
          <p className="font-['Montserrat'] text-[#94A3B8] font-bold">No hay FAQs todavía</p>
          <p className="font-['Montserrat'] text-[#CBD5E1] text-sm">Haz clic en "Nueva FAQ" para añadir la primera.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedFaqs.map((faq, i) => (
            <div
              key={faq._id}
              data-testid={`faq-row-${i}`}
              className={`bg-white rounded-2xl border p-5 transition-all ${faq.activa ? "border-[#E2E8F0]" : "border-[#E2E8F0] opacity-60"}`}
            >
              <div className="flex items-start gap-4">
                {/* Order controls */}
                <div className="flex flex-col gap-1 pt-0.5">
                  <button onClick={() => handleMoveOrder(faq, "up")} disabled={i === 0} data-testid={`faq-up-${i}`}
                    className="p-1 rounded hover:bg-[#F0F8FF] disabled:opacity-30 transition-colors">
                    <ChevronUp size={14} color="#475569" />
                  </button>
                  <button onClick={() => handleMoveOrder(faq, "down")} disabled={i === sortedFaqs.length - 1} data-testid={`faq-down-${i}`}
                    className="p-1 rounded hover:bg-[#F0F8FF] disabled:opacity-30 transition-colors">
                    <ChevronDown size={14} color="#475569" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="font-['Montserrat'] font-bold text-[#0F172A] text-sm leading-snug mb-1">{faq.pregunta}</p>
                  <p className="font-['Montserrat'] text-[#475569] text-sm line-clamp-2 leading-relaxed">{faq.respuesta}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => handleToggle(faq)} data-testid={`faq-toggle-visible-${i}`}
                    title={faq.activa ? "Ocultar" : "Mostrar"}
                    className={`p-2 rounded-lg transition-colors ${faq.activa ? "text-[#3CB44B] hover:bg-green-50" : "text-[#94A3B8] hover:bg-[#F8FAFC]"}`}>
                    {faq.activa ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => startEdit(faq)} data-testid={`faq-edit-${i}`}
                    className="p-2 rounded-lg text-[#0072A4] hover:bg-[#F0F8FF] transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => handleDelete(faq._id)} data-testid={`faq-delete-${i}`}
                    className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
